/**
 * AuditLogsPage Component
 *
 * Main page for viewing tenant audit logs with filtering and cursor-based pagination.
 * Follows the EltJobExecutionsPage pattern for layout and data fetching.
 */

import React, { useState, useCallback } from "react";
import { DashboardLayout } from "../../../components/layout/DashboardLayout";
import { Button } from "../../../components/ui/Button";
import { Alert } from "../../../components/ui/Alert";
import { AuditLogTable } from "../components/AuditLogTable";
import { AuditLogFilters, type AuditLogFilterValues } from "../components/AuditLogFilters";
import { AuditLogDetailsModal } from "../components/AuditLogDetailsModal";
import { useAuditLogs } from "../hooks/useAuditLogs";
import { useAuth } from "../../../contexts/AuthContext";
import { useLayoutProps } from "../../../hooks/useLayoutProps";
import { getErrorMessage } from "../../../lib/utils/errors";
import type { AuditLogEntry, AuditLogListQuery } from "../../../types/audit-log.types";

const PAGE_SIZE = 20;

const INITIAL_FILTERS: AuditLogFilterValues = {
  resourceType: "",
  action: "",
  startDate: "",
  endDate: "",
};

export const AuditLogsPage: React.FC = () => {
  const { tenant } = useAuth();
  const layoutProps = useLayoutProps();
  const tenantId = tenant?.tenantId || "";

  // Filter state
  const [filters, setFilters] = useState<AuditLogFilterValues>(INITIAL_FILTERS);

  // Pagination state — accumulated entries for "load more" pattern
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [accumulatedEntries, setAccumulatedEntries] = useState<AuditLogEntry[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Detail modal state
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null);

  // Build query from filters
  const query: AuditLogListQuery = {
    limit: PAGE_SIZE,
    cursor,
    ...(filters.resourceType && { resourceType: filters.resourceType }),
    ...(filters.action && { action: filters.action as AuditLogListQuery["action"] }),
    ...(filters.startDate && { startDate: new Date(filters.startDate).toISOString() }),
    ...(filters.endDate && { endDate: new Date(filters.endDate + "T23:59:59").toISOString() }),
  };

  const { auditLogs, pagination, isLoading, isFetching, error, refetch } = useAuditLogs(tenantId, query);

  // When new data arrives from a "load more", append to accumulated entries
  React.useEffect(() => {
    if (auditLogs.length > 0) {
      if (isLoadingMore) {
        setAccumulatedEntries((prev) => [...prev, ...auditLogs]);
        setIsLoadingMore(false);
      } else if (!cursor) {
        // Fresh load (no cursor = first page)
        setAccumulatedEntries(auditLogs);
      }
    } else if (!isLoading && !cursor) {
      setAccumulatedEntries([]);
    }
  }, [auditLogs, isLoading, cursor, isLoadingMore]);

  // Reset pagination when filters change
  const handleFilterChange = useCallback((newFilters: AuditLogFilterValues) => {
    setFilters(newFilters);
    setCursor(undefined);
    setAccumulatedEntries([]);
    setIsLoadingMore(false);
  }, []);

  // Load more entries
  const handleLoadMore = useCallback(() => {
    if (pagination.cursor) {
      setIsLoadingMore(true);
      setCursor(pagination.cursor);
    }
  }, [pagination.cursor]);

  // Refresh from the start
  const handleRefresh = useCallback(() => {
    setCursor(undefined);
    setAccumulatedEntries([]);
    setIsLoadingMore(false);
    refetch();
  }, [refetch]);

  const handleViewDetails = (entry: AuditLogEntry) => {
    setSelectedEntry(entry);
  };

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "var(--color-text-primary)" }}>Audit Log</h1>
          <p className="mt-1 text-sm" style={{ color: "var(--color-text-secondary)" }}>
            View a chronological record of all changes made in your tenant
          </p>
        </div>

        {/* Filters */}
        <AuditLogFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onRefresh={handleRefresh}
          isFetching={isFetching}
          totalCount={pagination.total}
          displayedCount={accumulatedEntries.length}
        />

        {/* Error */}
        {error && <Alert variant="error">{getErrorMessage(error)}</Alert>}

        {/* Table */}
        <AuditLogTable
          entries={accumulatedEntries}
          isLoading={isLoading && !isLoadingMore}
          onView={handleViewDetails}
        />

        {/* Load More */}
        {pagination.hasMore && (
          <div className="flex justify-center">
            <Button
              variant="secondary"
              onClick={handleLoadMore}
              disabled={isFetching}
            >
              {isFetching && isLoadingMore ? "Loading..." : "Load More"}
            </Button>
          </div>
        )}

        {/* Details Modal */}
        {selectedEntry && (
          <AuditLogDetailsModal
            isOpen={!!selectedEntry}
            onClose={() => setSelectedEntry(null)}
            entry={selectedEntry}
          />
        )}
      </div>
    </DashboardLayout>
  );
};
